import { resolveDynamicImageUrl } from '../../lib/imageUrl';
import { getOpportunityPrimaryCover, isRedirectOpportunity } from '../../lib/manproOpportunityMedia';
import type {
  BidDocumentDto,
  BidStatus,
  OwnerProjectDocumentDto,
  OwnerProjectGalleryImageDto,
  OwnerProjectMetadata,
  ProviderBidDto,
  ProviderOpportunityDto,
} from './types';

const formatCurrency = (value?: number | string | null) => {
  const numericValue = Number(value || 0);

  if (!Number.isFinite(numericValue) || numericValue <= 0) {
    return 'Budget belum diisi';
  }

  return new Intl.NumberFormat('id-ID', {
    style: 'currency',
    currency: 'IDR',
    maximumFractionDigits: 0,
  }).format(numericValue);
};

const formatDate = (value?: string | null) => {
  if (!value) return '-';

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat('id-ID', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(date);
};

const normalizeStatus = (status?: BidStatus | string | null): BidStatus => {
  if (status === 'denied') return 'rejected';
  if (status === 'pending') return 'submitted';

  if (status === 'accepted' || status === 'rejected' || status === 'withdrawn' || status === 'submitted') {
    return status;
  }

  return 'submitted';
};

const isDeadlinePassed = (deadline?: string | null) => {
  if (!deadline) return false;

  const deadlineDate = new Date(deadline);

  if (Number.isNaN(deadlineDate.getTime())) {
    return false;
  }

  deadlineDate.setHours(23, 59, 59, 999);

  return deadlineDate.getTime() < Date.now();
};

const isActiveProviderBidStatus = (status?: BidStatus | string | null) =>
  status === 'pending' || status === 'submitted' || status === 'accepted';

const getDocumentUrl = (document: OwnerProjectDocumentDto | BidDocumentDto) =>
  resolveDynamicImageUrl(document.file_path);

const getGalleryUrl = (image: OwnerProjectGalleryImageDto | string) =>
  typeof image === 'string'
    ? resolveDynamicImageUrl(image)
    : resolveDynamicImageUrl(image.url || image.file_path);

const normalizeGalleryImage = (
  image: OwnerProjectGalleryImageDto | string,
  index: number,
): OwnerProjectGalleryImageDto => {
  if (typeof image === 'string') {
    return {
      name: `Foto proyek ${index + 1}`,
      url: image,
      file_path: image,
      document_category: 'location_photo',
    };
  }

  return image;
};

const normalizeMetadataDocument = (
  item: OwnerProjectDocumentDto | string | Record<string, unknown>,
  index: number,
): OwnerProjectDocumentDto | null => {
  if (typeof item === 'string') {
    return {
      id: `metadata-document-${index}`,
      name: `Dokumen pendukung ${index + 1}`,
      file_path: item,
      document_category: 'other',
    };
  }

  const record = item as Record<string, unknown>;
  const rawPath = record.file_path || record.path || record.url || record.link || record.source_url;
  const filePath = typeof rawPath === 'string' ? rawPath : '';

  if (!filePath) return null;

  return {
    id: String(record.id || `metadata-document-${index}`),
    name: String(record.name || record.title || `Dokumen pendukung ${index + 1}`),
    file_path: filePath,
    file_type: typeof record.file_type === 'string' ? record.file_type : null,
    file_size: typeof record.file_size === 'number' ? record.file_size : null,
    document_category: typeof record.document_category === 'string'
      ? record.document_category
      : typeof record.category === 'string'
        ? record.category
        : 'other',
    uploaded_by: typeof record.uploaded_by === 'string' ? record.uploaded_by : null,
    created_at: typeof record.created_at === 'string' ? record.created_at : null,
    updated_at: typeof record.updated_at === 'string' ? record.updated_at : null,
  };
};

const getOpportunityDocuments = (opportunity: ProviderOpportunityDto) => {
  const apiDocuments = opportunity.owner_project_documents?.length
    ? opportunity.owner_project_documents
    : opportunity.documents ?? [];
  const metadataDocuments = (opportunity.metadata?.document_links ?? [])
    .map(normalizeMetadataDocument)
    .filter((document): document is OwnerProjectDocumentDto => Boolean(document));

  return apiDocuments.length ? apiDocuments : metadataDocuments;
};

const getOpportunityGalleryImages = (opportunity: ProviderOpportunityDto) => {
  const apiGalleryImages = (opportunity.gallery_images ?? []).map(normalizeGalleryImage);
  const metadataGalleryImages = (opportunity.metadata?.gallery_images ?? []).map(normalizeGalleryImage);
  const galleryImages = apiGalleryImages.length
    ? apiGalleryImages
    : metadataGalleryImages.length
      ? metadataGalleryImages
    : getOpportunityDocuments(opportunity)
        .filter((document) => document.document_category === 'location_photo')
        .map((document) => ({
          name: document.name,
          url: document.file_path,
          file_path: document.file_path,
          file_type: document.file_type,
          file_size: document.file_size,
          document_category: document.document_category,
        }));

  return galleryImages.map((image) => ({
    ...image,
    resolved_url: getGalleryUrl(image),
  }));
};

const getOwnerName = (opportunity?: ProviderOpportunityDto | null) => {
  const owner = opportunity?.owner;
  const name = [owner?.first_name, owner?.last_name].filter(Boolean).join(' ').trim();

  return name || owner?.email || 'Project Owner';
};

const getBudgetLabel = (opportunity?: ProviderOpportunityDto | null) => {
  if (!opportunity) return 'Budget belum diisi';

  if (opportunity.budget_range) return opportunity.budget_range;
  if (opportunity.budget_max) return formatCurrency(opportunity.budget_max);
  if (opportunity.budget_min) return `Mulai ${formatCurrency(opportunity.budget_min)}`;

  return 'Budget belum diisi';
};

const getLocationLabel = (opportunity?: ProviderOpportunityDto | null) =>
  [
    opportunity?.location,
    opportunity?.district,
    opportunity?.city,
    opportunity?.province,
  ]
    .filter(Boolean)
    .join(', ') || 'Lokasi belum diisi';

export type ProviderOpportunityCardView = {
  id: string;
  postId?: string;
  ownerProjectId?: string;
  title: string;
  category: string;
  description: string;
  location: string;
  addressDetail: string;
  budgetLabel: string;
  budgetMin?: number | null;
  budgetMax?: number | null;
  procurementType: string;
  procurementCode?: string | null;
  procurementMethod?: string | null;
  sourceLabel: string;
  deadline?: string | null;
  deadlineLabel: string;
  estimatedStartLabel: string;
  estimatedFinishLabel: string;
  duration: string;
  requirements: string[];
  metadata?: OwnerProjectMetadata | null;
  documents: OwnerProjectDocumentDto[];
  documentCount: number;
  galleryImages: Array<OwnerProjectGalleryImageDto & { resolved_url: string }>;
  coverImage: string;
  detailRequirements: string;
  googleMapsUrl?: string | null;
  isRedirectOpportunity: boolean;
  sourceUrl?: string | null;
  sourceName?: string | null;
  hasBid: boolean;
  providerBid?: ProviderBidDto | null;
  providerBidStatus?: BidStatus;
  ownerName: string;
  isDeadlinePassed: boolean;
  isFavorite: boolean;
  isOwnProject: boolean;
  createdAt?: string | null;
};

export type ProviderBidRowView = {
  id: string;
  postId?: string;
  ownerProjectId?: string;
  opportunity?: ProviderOpportunityDto | null;
  title: string;
  category: string;
  location: string;
  amount?: number | null;
  amountLabel: string;
  duration: string;
  status: BidStatus;
  statusLabel: string;
  note: string;
  methodStatement: string;
  commercialTerms: string;
  submittedAt?: string | null;
  submittedAtLabel: string;
  acceptedAt?: string | null;
  rejectedAt?: string | null;
  withdrawnAt?: string | null;
  documents: BidDocumentDto[];
  documentLinks: Array<BidDocumentDto & { resolved_url: string }>;
  canWithdraw: boolean;
  isAccepted: boolean;
  manproProjectId?: string | null;
};

export const providerBidStatusLabels: Record<BidStatus, string> = {
  pending: 'Menunggu Review',
  submitted: 'Menunggu Review',
  accepted: 'Diterima',
  denied: 'Ditolak',
  rejected: 'Ditolak',
  withdrawn: 'Ditarik',
};

// Mapper ini menjadi kontrak tengah agar halaman Provider tidak tersebar membaca struktur API mentah.
export const mapProviderOpportunityToCard = (
  opportunity: ProviderOpportunityDto,
): ProviderOpportunityCardView => {
  const documents = getOpportunityDocuments(opportunity);
  const galleryImages = getOpportunityGalleryImages(opportunity);
  const providerBidStatus = opportunity.provider_bid?.status
    ? normalizeStatus(opportunity.provider_bid.status)
    : undefined;
  const isRedirect = isRedirectOpportunity(opportunity);
  const hasActiveBid = Boolean(
    opportunity.provider_bid
      ? isActiveProviderBidStatus(opportunity.provider_bid.status)
      : opportunity.has_bid,
  );

  return {
    id: String(opportunity.id),
    postId: opportunity.post_id,
    ownerProjectId: opportunity.owner_project_id,
    title: opportunity.title || 'Project tanpa judul',
    category: opportunity.category || 'Kategori belum diisi',
    description: opportunity.description || 'Deskripsi project belum tersedia.',
    location: getLocationLabel(opportunity),
    addressDetail: opportunity.address_detail || '-',
    budgetLabel: getBudgetLabel(opportunity),
    budgetMin: opportunity.budget_min,
    budgetMax: opportunity.budget_max,
    procurementType: opportunity.procurement_type_label || opportunity.procurement_type || 'Tipe pengadaan belum diisi',
    procurementCode: opportunity.procurement_type,
    procurementMethod: opportunity.procurement_method,
    sourceLabel: opportunity.project_source_label || opportunity.source_name || 'Sumber belum diisi',
    deadline: opportunity.bid_deadline || opportunity.deadline,
    deadlineLabel: formatDate(opportunity.bid_deadline || opportunity.deadline),
    estimatedStartLabel: formatDate(opportunity.estimated_start),
    estimatedFinishLabel: formatDate(opportunity.estimated_finish),
    duration: opportunity.duration || '-',
    requirements: opportunity.requirements ?? [],
    metadata: opportunity.metadata,
    documents,
    documentCount: opportunity.documents_count ?? documents.length,
    galleryImages,
    // Cover Provider mengikuti kontrak media MANPRO:
    // 1) gunakan gallery_images dari API, 2) fallback ke dokumen location_photo,
    // 3) jika LPSE/JSON tidak punya foto, pilih cover lokal berdasarkan kategori project.
    coverImage: getOpportunityPrimaryCover(opportunity),
    detailRequirements: opportunity.detail_requirements || opportunity.metadata?.detail_requirements || '',
    googleMapsUrl: opportunity.google_maps_url || opportunity.metadata?.google_maps_url || null,
    isRedirectOpportunity: isRedirect,
    sourceUrl: opportunity.source_url,
    sourceName: opportunity.source_name,
    // Withdrawn/rejected bid tidak lagi dianggap bid aktif agar Provider bisa submit ulang ke project yang sama.
    hasBid: hasActiveBid,
    providerBid: opportunity.provider_bid,
    providerBidStatus,
    ownerName: getOwnerName(opportunity),
    isDeadlinePassed: isDeadlinePassed(opportunity.bid_deadline || opportunity.deadline),
    isFavorite: Boolean(opportunity.is_favorite),
    isOwnProject: Boolean(opportunity.is_own_project),
    createdAt: opportunity.created_at,
  };
};

// Mapper ini dipakai untuk halaman Penawaran Saya agar status, nominal, dan dokumen bid konsisten.
export const mapProviderBidToRow = (bid: ProviderBidDto): ProviderBidRowView => {
  const status = normalizeStatus(bid.status);
  const opportunity = bid.opportunity ?? null;
  const documents = bid.documents ?? [];

  return {
    id: String(bid.id),
    postId: bid.post_id,
    ownerProjectId: bid.owner_project_id || opportunity?.owner_project_id || opportunity?.id,
    opportunity,
    title: opportunity?.title || 'Project tanpa judul',
    category: opportunity?.category || 'Kategori belum diisi',
    location: getLocationLabel(opportunity),
    amount: bid.amount ?? bid.offered_price,
    amountLabel: formatCurrency(bid.amount ?? bid.offered_price),
    duration: bid.duration || '-',
    status,
    statusLabel: providerBidStatusLabels[status],
    note: bid.note || bid.provider_note || '-',
    methodStatement: bid.method_statement || '-',
    commercialTerms: bid.commercial_terms || '-',
    submittedAt: bid.submitted_at,
    submittedAtLabel: formatDate(bid.submitted_at),
    acceptedAt: bid.accepted_at,
    rejectedAt: bid.rejected_at,
    withdrawnAt: bid.withdrawn_at,
    documents,
    documentLinks: documents.map((document) => ({
      ...document,
      resolved_url: getDocumentUrl(document),
    })),
    canWithdraw: status === 'submitted' || bid.status === 'pending',
    isAccepted: status === 'accepted',
    manproProjectId: opportunity?.manpro_project_id,
  };
};
